import { computed, ref, watch } from "vue";
import type { Ref } from "vue";
import type { UpsertConstruction } from "@/types/Views/CreateConstruction/NewConstruction";

export const getChangesFromOriginal = (current: UpsertConstruction, orig: UpsertConstruction): string[] => {
  const changes: string[] = [];

  const currentName = current.names[0]?.label ?? "";
  const origName = orig.names[0]?.label ?? "";
  if (currentName !== origName) changes.push(`name: "${origName}" → "${currentName}"`);

  const currentDesc = current.descriptions[0]?.label ?? "";
  const origDesc = orig.descriptions[0]?.label ?? "";
  if (currentDesc !== origDesc) changes.push(`description: "${origDesc}" → "${currentDesc}"`);

  if (current.declaredUnit.unitId !== orig.declaredUnit.unitId)
    changes.push(`declaredUnit: ${orig.declaredUnit.unitId} → ${current.declaredUnit.unitId}`);

  if (current.materials.length !== orig.materials.length) {
    changes.push(`materials count: ${orig.materials.length} → ${current.materials.length}`);
  } else {
    current.materials.forEach((mat, i) => {
      if (JSON.stringify(mat) !== JSON.stringify(orig.materials[i]))
        changes.push(`material[${i}] (${mat.material?.name ?? mat.materialId}) changed`);
    });
  }

  if (current.inhomogeneousLayers.length !== orig.inhomogeneousLayers.length) {
    changes.push(`inhomogeneousLayers count: ${orig.inhomogeneousLayers.length} → ${current.inhomogeneousLayers.length}`);
  } else {
    current.inhomogeneousLayers.forEach((layer, i) => {
      if (JSON.stringify(layer) !== JSON.stringify(orig.inhomogeneousLayers[i])) changes.push(`inhomogeneousLayer[${i}] changed`);
    });
  }

  if (current.categoryId !== orig.categoryId) changes.push(`categoryId: "${orig.categoryId}" → "${current.categoryId}"`);

  return changes;
};

export const useConstructionDirtyState = (construction: Ref<UpsertConstruction>) => {
  const originalConstruction = ref<UpsertConstruction | null>(null);

  const isDirty = computed(() => {
    if (!originalConstruction.value) return false;
    return getChangesFromOriginal(construction.value, originalConstruction.value).length > 0;
  });

  const initOriginal = () => {
    originalConstruction.value = JSON.parse(JSON.stringify(construction.value));
  };

  watch(
    construction,
    (current) => {
      if (!originalConstruction.value) return;
      getChangesFromOriginal(current, originalConstruction.value);
    },
    { deep: true }
  );

  return { isDirty, initOriginal };
};
